/**
 * Sesión en React.
 *
 * Envuelve `api/session.ts`, que es quien guarda el token en memoria, y expone el usuario
 * actual al árbol de componentes.
 *
 * Al montar intenta UN refresco silencioso: si la cookie `httpOnly` sigue viva, el usuario
 * entra directo sin volver a escribir la contraseña. Mientras tanto la app muestra un estado
 * de carga y NO el formulario de acceso — enseñar el login durante medio segundo a alguien
 * que ya tiene sesión es un parpadeo que parece un error.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginAudience, Permission, Role, SessionUser } from '@naar/shared';
import { can } from '@naar/shared';
import {
  getCurrentUser,
  login as doLogin,
  logout as doLogout,
  refreshSession,
  subscribeToSession,
} from '../api/session';

interface AuthContextValue {
  user: SessionUser | null;
  /** `true` mientras se resuelve el refresco inicial. */
  loading: boolean;
  role: Role | null;
  login: (email: string, password: string, audience?: LoginAudience) => Promise<void>;
  logout: () => Promise<void>;
  /** Permisos declarativos, los MISMOS que aplica la API (`@naar/shared`). */
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(getCurrentUser());
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // El token vive fuera de React; esto mantiene el árbol al día cuando cambia.
  useEffect(() => subscribeToSession(setUser), []);

  useEffect(() => {
    let cancelled = false;
    void refreshSession().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, audience?: LoginAudience) => {
      await doLogin(email, password, audience);
      /**
       * Se vacía la caché al entrar. Sin esto, si el usuario anterior de esta pestaña era de
       * otra organización, TanStack Query serviría sus datos desde caché durante unos
       * segundos: exactamente la fuga que la RLS impide en el servidor.
       */
      queryClient.clear();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await doLogout();
    } finally {
      /**
       * La caché se vacía en `finally` por el mismo motivo que `doLogout()` limpia el token en
       * `finally`: si la petición de salida falla por red, la sesión local se cierra igual, y
       * dejar en caché los inmuebles del usuario anterior significa que el siguiente que entre en
       * esta pestaña la vería pintada durante unos segundos.
       */
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      role: user?.role ?? null,
      login,
      logout,
      can: (permission: Permission) => can(user?.role ?? null, permission),
    }),
    [user, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return context;
}

/**
 * Atajo para ocultar acciones que la API va a rechazar.
 *
 * Que la API lo rechace es OBLIGATORIO; que la UI no lo ofrezca es lo que hace que la
 * aplicación no parezca rota. Las dos comprobaciones usan la misma tabla de `@naar/shared`,
 * así que no pueden divergir.
 */
export function useCan(permission: Permission): boolean {
  return useAuth().can(permission);
}
